import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { useNavigate } from "@tanstack/react-router"

import { useChat } from "@/features/chat"
import { advisorsList, AdvisorAdsCard } from "@/features/advisors"
import { bumpUsage } from "@/shared/lib/usage-stats"
import { cn } from "@/lib/utils"

import NumberDecoration from "@/assets/img/today/numerology/number_decoration.webp"
import NumerologyAds from "@/assets/img/today/numerology/numerology_ads.webp"
import TapTip from "@/assets/svg/today/tap_tip.svg?react"

import { NUMBER_OF_THE_DAY } from "../../config/numerology-data"
import { useAstroToday } from "../../hooks/use-astro-today"
import { useOpenedScreens } from "../../hooks/use-opened-screens"
import { TodayTimer } from "../today-timer"
import { TodayLoader } from "../today-loader"
import { PersonalizedReports } from "../personalized-reports"
import { ADVISOR_PRESETS } from "../../config/advisor-presets"

const SCREEN = "numerology"

/** Number-of-the-day category. Ported from Today/Numerology. */
export function NumerologyCategory() {
  const navigate = useNavigate()
  const { setSelectedAdvisor, setPreSelectedChatMessage } = useChat()
  const { data, meta, loading } = useAstroToday(["numerology"])
  const { isOpened, open } = useOpenedScreens()

  const advisor = advisorsList[3]
  const numerology = data?.numerology
  const dayData = numerology ? NUMBER_OF_THE_DAY[numerology.content_id] : undefined
  const opened = isOpened(SCREEN)

  const goToChat = (message?: string) => {
    if (message) setPreSelectedChatMessage(message)
    setSelectedAdvisor(advisor)
    navigate({ to: WebappRoutes.READINGS })
  }

  if (loading) return <TodayLoader />
  if (!numerology || !dayData) return <TodayLoader empty />

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={
          opened
            ? undefined
            : () => {
                open(SCREEN)
                bumpUsage("numerology")
              }
        }
        className={cn(
          "relative mx-4 mb-8 mt-10 flex items-center justify-center",
          !opened && "cursor-pointer",
        )}
      >
        <img src={NumberDecoration} alt="" className="select-none" />
        <span className="absolute font-display text-[84px] font-semibold text-white opacity-60 mix-blend-plus-lighter">
          {opened ? numerology.personal_day_number : "?"}
        </span>
      </button>

      {!opened ? (
        <div className="mx-4 flex items-center justify-center gap-3 rounded-2xl bg-purple-light/5 px-4 py-3">
          <TapTip className="h-[42px] w-[35px] shrink-0" />
          <p className="font-body text-sm text-purple-light/70">
            Take a moment and tap to reveal your personal{" "}
            <span className="font-medium text-purple-light">Number of the Day</span>
          </p>
        </div>
      ) : (
        <div className="w-full animate-in fade-in duration-700">
          <div className="px-4">
            <h2 className="mb-2.5 font-display text-2xl font-medium text-purple-light">
              {dayData.name}
            </h2>
            <p className="mb-2.5 font-body text-[10px] font-medium uppercase text-purple-light/40">
              Key themes of the day:
            </p>
            <div className="mb-4 flex flex-wrap items-center gap-[5px]">
              {dayData.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-[23px] border border-purple-light/20 px-3 py-1.5 font-body text-sm text-purple-light/70"
                >
                  {tag}
                </span>
              ))}
            </div>
            <p className="font-body text-base text-purple-light/70">{dayData.description}</p>

            <h3 className="mb-2 mt-10 font-display text-2xl font-medium text-purple-light">
              This number holds meaning
            </h3>
            <p className="mb-6 font-body text-base text-purple-light/70">
              Relationships, goals, inner balance — discover what today’s number reveals about your
              path.
            </p>

            <AdvisorAdsCard
              advisor={advisor}
              message={`Welcome. Today’s number is [${numerology.personal_day_number}] — full of wisdom and quiet clarity. Which area of life would you like to explore through its lens?`}
              presets={[...ADVISOR_PRESETS.numerology]}
              onPresetClick={goToChat}
            />
          </div>

          <PersonalizedReports />

          <h3 className="mb-2 mt-10 text-center font-display text-2xl font-medium text-purple-light">
            Today’s number is yours
          </h3>
          <div className="-my-2 flex items-center justify-center">
            <img src={NumerologyAds} alt="" className="select-none" />
          </div>
          <p className="mx-4 mb-4 text-center font-body text-base text-purple-light/70">
            Today’s number is part of your path.
            <br /> Whatever it may be —{" "}
            <span className="font-medium text-white">it’s yours for a reason</span>.
          </p>
          <div className="mb-10 px-4">
            <button
              type="button"
              onClick={() => goToChat()}
              className="flex h-14 w-full items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
            >
              Talk to a Numerologist
            </button>
          </div>

          {meta && (
            <div className="mx-4 mt-6 flex items-center justify-between gap-4 border-t border-purple-light/20 pt-6">
              <p className="font-body text-base text-purple-light/70">
                Come back tomorrow to uncover your next number
              </p>
              <TodayTimer target={meta.next_sync} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
